import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  PlusCircle,
  ArrowDownCircle,
  ArrowUpCircle,
  Users,
  TrendingUp,
  Clock,
  Timer,
  AlertCircle,
  Activity,
} from 'lucide-react';

const STAT_CARDS = [
  {
    label: 'Net Balance',
    icon: PlusCircle,
    gradient: 'from-yellow-500/20 to-orange-500/10',
    border: 'border-yellow-500/20',
    iconBg: 'bg-yellow-500/15',
    iconColor: 'text-yellow-400',
    valueColor: 'text-yellow-300',
    key: 'totalRevenue',
  },
  {
    label: 'Total Deposit',
    icon: ArrowDownCircle,
    gradient: 'from-cyan-500/20 to-blue-500/10',
    border: 'border-cyan-500/20',
    iconBg: 'bg-cyan-500/15',
    iconColor: 'text-cyan-400',
    valueColor: 'text-cyan-300',
    key: 'totalDeposit',
  },
  {
    label: 'Total Withdraw',
    icon: ArrowUpCircle,
    gradient: 'from-pink-500/20 to-red-500/10',
    border: 'border-pink-500/20',
    iconBg: 'bg-pink-500/15',
    iconColor: 'text-pink-400',
    valueColor: 'text-pink-300',
    key: 'totalWithdraw',
  },
  {
    label: 'Total Users',
    icon: Users,
    gradient: 'from-green-500/20 to-emerald-500/10',
    border: 'border-green-500/20',
    iconBg: 'bg-green-500/15',
    iconColor: 'text-green-400',
    valueColor: 'text-green-300',
    key: 'userCount',
  },
  {
    label: "Users' Total Profit",
    icon: TrendingUp,
    gradient: 'from-fuchsia-500/20 to-pink-500/10',
    border: 'border-fuchsia-500/20',
    iconBg: 'bg-fuchsia-500/15',
    iconColor: 'text-fuchsia-400',
    valueColor: 'text-fuchsia-300',
    key: 'totalUsersProfit',
  },
];

const fmtMoney = v =>
  v == null ? 'N/A' : `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const formatValue = (value, label, loading, error) => {
  if (loading) return <span className="inline-block w-16 h-6 bg-white/10 rounded animate-pulse" />;
  if (error) return <span className="text-red-400 text-base">—</span>;
  if (value === null || value === undefined) return 'N/A';
  const isMoney = ['Balance', 'Deposit', 'Withdraw', 'Profit'].some(k => label.includes(k));
  if (isMoney) return `$${parseFloat(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return value.toLocaleString();
};

const StatCard = ({ card, value, loading, error, linkTo }) => {
  const Icon = card.icon;
  const content = (
    <div className={`relative group flex items-center gap-4 p-5 rounded-2xl border bg-gradient-to-br ${card.gradient} ${card.border} backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-black/30 overflow-hidden`}>
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/[0.02]" />
      <div className={`shrink-0 w-12 h-12 rounded-xl ${card.iconBg} flex items-center justify-center`}>
        <Icon className={card.iconColor} size={22} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium text-gray-500 mb-1 truncate">{card.label}</p>
        <p className={`text-2xl font-bold tracking-tight ${card.valueColor}`}>
          {formatValue(value, card.label, loading, error)}
        </p>
      </div>
    </div>
  );
  return linkTo ? <Link to={linkTo}>{content}</Link> : <div>{content}</div>;
};

const statusPill = (status = '') => {
  const s = status.toLowerCase();
  if (s === 'paid' || s === 'completed')
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-500/10 border border-green-500/20 text-green-400">Paid</span>;
  if (s === 'rejected')
    return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-500/10 border border-red-500/20 text-red-400">Rejected</span>;
  return <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 border border-amber-500/20 text-amber-400">Pending</span>;
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    userCount: null, totalRevenue: null, totalDeposit: null,
    totalWithdraw: null, pendingDepositCount: null, pendingWithdrawCount: null, totalUsersProfit: null,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const cfg = { headers: { Authorization: `Bearer ${token}` } };

        const [userRes, depTotal, wdTotal, depReview, wdReview, profitRes] = await Promise.all([
          axios.get('/api/users/count', cfg),
          axios.get('/api/deposits/total-amount', cfg),
          axios.get('/api/withdrawals/total-amount', cfg),
          axios.get('/api/deposits/count/to-be-reviewed', cfg),
          axios.get('/api/withdrawals/count/to-be-reviewed', cfg),
          axios.get('/api/users/total-profit', cfg),
        ]);

        const dep = depTotal.data.total ?? 0;
        const wd  = wdTotal.data.total ?? 0;
        setStats({
          userCount: userRes.data.count,
          totalRevenue: dep - wd,
          totalDeposit: dep,
          totalWithdraw: wd,
          pendingDepositCount: depReview.data.count,
          pendingWithdrawCount: wdReview.data.count,
          totalUsersProfit: profitRes.data.totalProfit,
        });
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    const loadRecent = async () => {
      try {
        const token = localStorage.getItem('token');
        const cfg = { headers: { Authorization: `Bearer ${token}` } };
        const [depsRes, wdsRes] = await Promise.all([
          axios.get('/api/deposits/admin', cfg),
          axios.get('/api/withdrawals/admin', cfg),
        ]);
        const deps = (depsRes.data || []).slice(0, 8).map(d => ({ ...d, _type: 'deposit' }));
        const wds  = (wdsRes.data  || []).slice(0, 8).map(w => ({ ...w, _type: 'withdrawal' }));
        const combined = [...deps, ...wds]
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 8);
        setRecentActivity(combined);
      } catch {
        // non-critical, ignore
      } finally {
        setActivityLoading(false);
      }
    };

    load();
    loadRecent();
  }, []);

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white tracking-tight">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Platform overview and key metrics</p>
      </div>

      {error && (
        <div className="mb-6 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
          <AlertCircle size={16} />
          Failed to load some metrics: {error}
        </div>
      )}

      {/* Stat grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {STAT_CARDS.map(card => (
          <StatCard key={card.key} card={card} value={stats[card.key]} loading={loading} error={error} />
        ))}

        <StatCard
          card={{
            label: 'Pending Deposits',
            icon: Clock,
            gradient: 'from-cyan-500/20 to-sky-500/10',
            border: 'border-cyan-500/20',
            iconBg: 'bg-cyan-500/15',
            iconColor: 'text-cyan-400',
            valueColor: 'text-cyan-300',
            key: 'pendingDepositCount',
          }}
          value={stats.pendingDepositCount}
          loading={loading}
          error={error}
          linkTo="/admin/deposit-requests"
        />

        <StatCard
          card={{
            label: 'Pending Withdrawals',
            icon: Timer,
            gradient: 'from-pink-500/20 to-rose-500/10',
            border: 'border-pink-500/20',
            iconBg: 'bg-pink-500/15',
            iconColor: 'text-pink-400',
            valueColor: 'text-pink-300',
            key: 'pendingWithdrawCount',
          }}
          value={stats.pendingWithdrawCount}
          loading={loading}
          error={error}
          linkTo="/admin/withdraw-requests"
        />
      </div>

      {/* Quick Actions */}
      <div className="mt-10">
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-widest mb-4">Quick Actions</h2>
        <div className="flex flex-wrap gap-3">
          <Link to="/admin/deposit-requests" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/8 text-sm text-gray-300 hover:text-white hover:bg-white/8 hover:border-white/15 transition-all">
            <ArrowDownCircle size={16} className="text-cyan-400" /> Deposit Requests
          </Link>
          <Link to="/admin/withdraw-requests" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/8 text-sm text-gray-300 hover:text-white hover:bg-white/8 hover:border-white/15 transition-all">
            <ArrowUpCircle size={16} className="text-pink-400" /> Withdraw Requests
          </Link>
          <Link to="/admin/users" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/8 text-sm text-gray-300 hover:text-white hover:bg-white/8 hover:border-white/15 transition-all">
            <Users size={16} className="text-green-400" /> View Users
          </Link>
          <Link to="/admin/add-app" className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-sm text-cyan-400 hover:bg-cyan-500/15 transition-all">
            <PlusCircle size={16} /> Add New App
          </Link>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="mt-10">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-widest flex items-center gap-2">
            <Activity size={14} /> Recent Activity
          </h2>
          <div className="flex gap-2">
            <Link to="/admin/deposit-requests" className="text-xs text-gray-600 hover:text-cyan-400 transition-colors">Deposits →</Link>
            <Link to="/admin/withdraw-requests" className="text-xs text-gray-600 hover:text-pink-400 transition-colors">Withdrawals →</Link>
          </div>
        </div>

        <div className="rounded-2xl border border-white/8 overflow-hidden bg-[#0d1526]/60">
          {activityLoading ? (
            <div className="space-y-px">
              {[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-white/3 animate-pulse" />)}
            </div>
          ) : recentActivity.length === 0 ? (
            <div className="py-12 text-center text-gray-600 text-sm">No recent activity.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-white/6 bg-white/[0.02]">
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                  <th className="px-4 py-3 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.04]">
                {recentActivity.map(item => (
                  <tr key={`${item._type}-${item._id}`} className="hover:bg-white/[0.015] transition-colors">
                    <td className="px-4 py-3">
                      {item._type === 'deposit' ? (
                        <span className="inline-flex items-center gap-1.5 text-xs text-cyan-400">
                          <ArrowDownCircle size={12} /> Deposit
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs text-pink-400">
                          <ArrowUpCircle size={12} /> Withdraw
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-white">
                      {item.user?.username || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-white">
                      {fmtMoney(item.amount)}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden sm:table-cell whitespace-nowrap">
                      {item.createdAt ? new Date(item.createdAt).toLocaleString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      {statusPill(item.status)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
